import * as Sentry from '@sentry/react'
import { useState, type FC } from 'react'
import { Dropdown } from 'react-bootstrap'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { Button } from '../../../components/Button/Button'
import { CustomPaginationComponent } from '../../../components/DataTable/CustomPaginationComponent'
import { Spinner } from '../../../components/Spinner/Spinner'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { BookingStatus, type SchedulingFilters, type Booking, type SchedulingEventType } from '../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useArchiveBookingMutation, useGetBookingsQuery, useGetSchedulingEventTypesQuery, useSendReminderMutation } from '../../../store/scheduling/scheduling'
import { addToast } from '../../../store/toast/toastSlice'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { formatSentDate, getLinkCopiedAgoLabel, getSentAgoLabel } from '../bookingFormat'
import { BookingActionMenu } from '../rows/BookingActionMenu'
import { BookingRow } from '../rows/BookingRow'
import { ContactChips } from '../rows/ContactChips'
import { ReminderStatus } from '../rows/ReminderStatus'
import { ArchiveBookingDialog } from '../shared/ArchiveBookingDialog'
import { useSchedulingTemplatesEnabled } from '../shared/useSchedulingTemplatesEnabled'
import { SendReminderModal } from '../sendReminder/SendReminderModal'
import { clientDisplayName } from '../shared/schedulingDisplay'
import { SchedulingEmptyState } from './SchedulingEmptyState'
import { SchedulingTabErrorCard } from './SchedulingTabErrorCard'
import { toBookingsFilterArgs } from '../bookingTabs'
import { firstApiErrorMessage, isFetchBaseQueryError } from '../../../models/ApiErrors'

const allRecipientsUnsubscribed = (booking: Booking): boolean => {
  const emailed = booking.clients.filter((client) => client.email)
  return emailed.length > 0 && emailed.every((client) => client.unsubscribedAt)
}

const sendReminderFailureMessage = (error: unknown): string =>
  isFetchBaseQueryError(error) && error.status === 409 ? (firstApiErrorMessage(error) ?? 'Please try again.') : 'Please try again.'

interface WaitingDateBlockProps {
  booking: Booking
}

const WaitingDateBlock: FC<WaitingDateBlockProps> = ({ booking }) => {
  const isSent = booking.invitationSent === true
  const at = isSent ? (booking.invitationSentAt ?? booking.updatedAt) : booking.createdAt
  return (
    <div className='d-flex flex-column flex-1 text-nowrap'>
      <div className='lead-small'>{isSent ? getSentAgoLabel(at) : getLinkCopiedAgoLabel(at)}</div>
      <div className='paragraph-small zocks-text-gray-700'>{formatSentDate(new Date(at))}</div>
    </div>
  )
}

const WaitingSummary: FC<{ booking: Booking; eventType?: SchedulingEventType }> = ({ booking, eventType }) => (
  <div className='d-flex align-items-center flex-4 mw-0 gap-4'>
    <div className='d-flex flex-column flex-grow-1 mw-0 zocks-gap-0.125'>
      <div className='bold text-truncate'>{booking.title ?? eventType?.name}</div>
      <div className='d-flex align-items-center flex-wrap zocks-gap-0.25 paragraph-small zocks-text-gray-700'>
        <ContactChips clients={booking.clients} />
        {eventType ? (
          <>
            <span className='zocks-text-gray-600'>·</span>
            {eventType.name}
          </>
        ) : null}
      </div>
    </div>
    <ReminderStatus reminder={booking.reminders} />
  </div>
)

const WaitingBookings: FC<{ filters: SchedulingFilters; onOpenReminder: (booking: Booking) => void }> = ({ filters, onOpenReminder }) => {
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const {
    data: bookingsResponse,
    isFetching,
    isError,
    refetch,
  } = useGetBookingsQuery({
    ...toBookingsFilterArgs(filters),
    accountId: authenticatedUser.accountId,
    status: BookingStatus.AWAITING_REPLY,
    pageRequest: { page: page - 1, size: pageSize },
  })
  const bookings = bookingsResponse?.data
  const totalElements = bookingsResponse?.page.totalElements ?? 0
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const [sendReminder, { isLoading: isSending, originalArgs }] = useSendReminderMutation()
  const [archiveBooking, { isLoading: isArchiving, originalArgs: archiveArgs }] = useArchiveBookingMutation()
  const [archiving, setArchiving] = useState<Booking | null>(null)
  const templates = useSchedulingTemplatesEnabled()

  if (isFetching) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return <SchedulingTabErrorCard title="Couldn't load booking links" onRetry={refetch} />
  }

  if (!Array.isArray(bookings) || bookings.length === 0) {
    return <SchedulingEmptyState title='No booking links' subtitle='Booking links waiting on a client appear here.' />
  }

  const eventTypeById = new Map((eventTypes ?? []).map((eventType) => [eventType.id, eventType]))
  const waiting = [...bookings].sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())

  const handleSendReminder = async (booking: Booking) => {
    const bookingId = booking.bookingId
    const result = await sendReminder({ accountId: authenticatedUser.accountId, bookingId })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not send the reminder', messages: [sendReminderFailureMessage(result.error)] }))
    } else {
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_REMINDER_SENT, eventDetails: { bookingId } })
      dispatch(addToast({ variant: 'success', title: 'Reminder sent', messages: [`A reminder for ${booking.title ?? 'the booking'} was sent.`] }))
    }
  }

  const handleCopyBookingLink = async (booking: Booking) => {
    const clientsWithLink = booking.clients.filter((client) => client.inviteUrl)
    if (clientsWithLink.length === 0) {
      dispatch(addToast({ variant: 'info', title: 'No booking link yet', messages: ['This invitation doesn’t have a shareable link.'] }))
      return
    }
    const text =
      clientsWithLink.length === 1
        ? clientsWithLink[0].inviteUrl!
        : clientsWithLink.map((client) => `${clientDisplayName(client)} - ${client.inviteUrl}`).join('\n')
    try {
      await navigator.clipboard.writeText(text)
    } catch (error) {
      Sentry.captureException(error)
      dispatch(addToast({ variant: 'error', title: 'Could not copy the link', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_LINK_COPIED, eventDetails: { source: 'waiting', bookingId: booking.bookingId } })
    dispatch(
      clientsWithLink.length === 1
        ? addToast({ variant: 'success', title: 'Booking link copied', messages: ['Paste it anywhere to share with the client.'] })
        : addToast({ variant: 'success', title: 'Booking links copied', messages: ['Each client has their own link — share them individually.'] }),
    )
  }

  const handleViewBookingPage = (booking: Booking) => {
    const link = booking.clients.find((client) => client.inviteUrl)?.inviteUrl
    if (!link) {
      dispatch(addToast({ variant: 'info', title: 'No booking page yet', messages: [`This invitation doesn't have a booking page link.`] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_PAGE_VIEWED, eventDetails: { source: 'waiting' } })
    window.open(link, '_blank', 'noopener,noreferrer')
  }

  const handleArchiveBooking = async () => {
    if (!archiving) {
      return
    }
    const bookingId = archiving.bookingId
    const result = await archiveBooking({ accountId: authenticatedUser.accountId, bookingId })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not archive the booking', messages: ['Please try again.'] }))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_ARCHIVED, eventDetails: { bookingId, source: 'waiting' } })
    dispatch(addToast({ variant: 'success', title: 'Booking archived', messages: [`${archiving.title ?? 'The booking'} was archived.`] }))
    setArchiving(null)
  }

  return (
    <div className='d-flex flex-column gap-2'>
      {waiting.map((booking) => {
        const unsubscribed = allRecipientsUnsubscribed(booking)
        return (
          <BookingRow
            key={booking.bookingId}
            booking={booking}
            timeBlock={<WaitingDateBlock booking={booking} />}
            summary={<WaitingSummary booking={booking} eventType={booking.eventTypeId ? eventTypeById.get(booking.eventTypeId) : undefined} />}
          >
            <Tooltip title={unsubscribed ? 'Every recipient has unsubscribed from reminders about this booking' : undefined}>
              <span tabIndex={unsubscribed ? 0 : undefined}>
                <Button
                  variant='grayButton'
                  disabled={unsubscribed || templates.isLoading}
                  loading={isSending && originalArgs?.bookingId === booking.bookingId}
                  onClick={() => (templates.isEnabled ? onOpenReminder(booking) : handleSendReminder(booking))}
                >
                  Send reminder
                </Button>
              </span>
            </Tooltip>
            <BookingActionMenu ariaLabel={`${booking.title ?? 'Booking'} actions`}>
              <Dropdown.Item onClick={() => handleCopyBookingLink(booking)}>
                <i className='bi bi-copy me-1' /> Copy booking link
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleViewBookingPage(booking)}>
                <i className='bi bi-box-arrow-up-right me-1' /> View booking page
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item className='text-danger' onClick={() => setArchiving(booking)}>
                <i className='bi bi-archive me-1' /> Archive
              </Dropdown.Item>
            </BookingActionMenu>
          </BookingRow>
        )
      })}
      <CustomPaginationComponent
        rowsPerPage={pageSize}
        rowCount={totalElements}
        currentPage={page}
        onChangePage={setPage}
        onChangeRowsPerPage={(newPageSize) => {
          setPage(1)
          setPageSize(newPageSize)
        }}
      />
      {archiving ? (
        <ArchiveBookingDialog
          isLoading={isArchiving && archiveArgs?.bookingId === archiving.bookingId}
          onConfirm={handleArchiveBooking}
          onCancel={() => setArchiving(null)}
        />
      ) : null}
    </div>
  )
}

export const WaitingTab: FC<{ filters: SchedulingFilters }> = ({ filters }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId })
  const [reminding, setReminding] = useState<Booking | null>(null)

  return (
    <>
      <WaitingBookings filters={filters} onOpenReminder={setReminding} />
      {reminding ? (
        <SendReminderModal
          booking={reminding}
          eventType={eventTypes?.find((eventType) => eventType.id === reminding.eventTypeId)}
          onHide={() => setReminding(null)}
        />
      ) : null}
    </>
  )
}
